/**
 * MediaHero — wide image hero with dark overlay + centred title/subtitle.
 *
 * Extracted from `IhsVenueReferee.hero` (Phase 0b). Renders a fixed-
 * height image block; on missing image URL or load error, the `<img>`
 * tag is hidden and a `TeamColorSvgHero` (or solid gradient) shows
 * through behind the overlay so there's never a flash of broken image.
 *
 * Shape:
 *   { imageUrl?: string;
 *     fallback?: 'team-colors' | 'gradient';
 *     homeKey?: string; awayKey?: string; eventId?: string;
 *     title: string; subtitle?: string; heightPx?: number }
 *
 * NOTE: HeroCard / DefaultCard / VideoArticleCard have their own
 * bespoke image-area shape — they are NOT migrated to MediaHero in this
 * pass (decision in Phase 0b spec).
 */

import { useState, type ReactElement, type SyntheticEvent } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import { TeamColorSvgHero } from '../atoms/TeamColorSvgHero.js';
import styles from './MediaHero.module.css';

const dataShape = z.object({
  imageUrl: z.string().url().optional(),
  /** Fallback when no image / image errors. Default 'gradient'. */
  fallback: z.enum(['team-colors', 'gradient']).optional(),
  /** Required when fallback='team-colors'. */
  homeKey: z.string().optional(),
  awayKey: z.string().optional(),
  /** Stable id used by TeamColorSvgHero to pick a pattern. */
  eventId: z.string().optional(),
  title: z.string(),
  subtitle: z.string().optional(),
  /** Optional pixel height. Default 96. */
  heightPx: z.number().int().positive().optional(),
});

export const META: PrimitiveMeta = {
  name: 'media-hero',
  description:
    'Full-width image hero with dark overlay + centred title/subtitle.',
  composable: true,
  dataShape,
  densities: ['hero'],
  breakpoints: { mobile: true, wide: true },
  animation: 'fade-in',
};

interface MediaHeroProps {
  imageUrl?: string;
  /** Fallback when no image / image errors. Default 'gradient'. */
  fallback?: 'team-colors' | 'gradient';
  /** Required when `fallback='team-colors'`. */
  homeKey?: string;
  awayKey?: string;
  /** Stable id used by `TeamColorSvgHero` to pick a pattern. */
  eventId?: string;
  title: string;
  subtitle?: string;
  /** Optional pixel height. Default 96. */
  heightPx?: number;
}

/**
 * Compact-by-default law (2026-07-31): no media hero renders taller than
 * this, regardless of what a caller (including the layout LLM) asks for.
 * The hero sets the scene; it never eats a third of the viewport.
 */
const MAX_HEIGHT_PX = 160;

export function MediaHero({
  imageUrl,
  fallback = 'gradient',
  homeKey,
  awayKey,
  eventId,
  title,
  subtitle,
  heightPx = 96,
}: MediaHeroProps): ReactElement {
  const cappedHeightPx = Math.min(heightPx, MAX_HEIGHT_PX);
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = !!imageUrl && !imgFailed;

  const handleImageError = (
    e: SyntheticEvent<HTMLImageElement, Event>,
  ): void => {
    e.preventDefault();
    setImgFailed(true);
  };

  return (
    <div
      className={`${styles.hero}${hasImage ? '' : ` ${styles.heroNoImg}`}`}
      style={{ height: `${cappedHeightPx}px` }}
    >
      {fallback === 'team-colors'
        && !hasImage
        && homeKey !== undefined
        && awayKey !== undefined
        && eventId !== undefined ? (
        <TeamColorSvgHero
          homeKey={homeKey}
          awayKey={awayKey}
          eventId={eventId}
          ariaLabel={title}
          className={styles.heroImg}
        />
      ) : null}
      {hasImage ? (
        <img
          src={imageUrl}
          alt={title}
          className={styles.heroImg}
          onError={handleImageError}
          loading="lazy"
          decoding="async"
        />
      ) : null}
      <div className={styles.heroOverlay} aria-hidden="true" />
      <div className={styles.heroText}>
        <div className={styles.heroName}>{title}</div>
        {subtitle !== undefined ? (
          <div className={styles.heroMeta}>{subtitle}</div>
        ) : null}
      </div>
    </div>
  );
}

export default MediaHero;
